import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  ChevronLeft,
  PackageMinus,
  PencilLine,
  RefreshCw,
  Search,
  Trash,
} from "lucide-react";
import { Button } from "../utils/components/ui/button";
import Loading from "../components/common/LoaderCommon";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  useDeletePurchaseEntryReturn,
  useInfiniteGetAllPurchaseReturn,
} from "../service/purchaseEntryReturnService";
import { toast } from "react-toastify";
import Modal from "../components/common/Modal";
import AddNewPurchaseReturn from "../components/forms/AddnewPurchaseReturn";
import ConfirmPopup from "../components/ConfirmPopup";

function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-[0_14px_36px_rgba(26,94,249,0.07)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[#7B8797]">{title}</p>
          <p className="mt-3 text-[28px] font-semibold tracking-[-0.02em] text-[#1A2B49]">
            {value}
          </p>
          <p className="mt-2 text-sm text-[#8D98A7]">{subtitle}</p>
        </div>
        <div className="rounded-[16px] bg-[#EEF4FF] p-3 text-[#1A5EF9]">
          {icon}
        </div>
      </div>
    </div>
  );
}

function PurchaseReturn() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [debouncedSearchKey] = useDebounce(searchKey, 500);
  const [currentPage, setCurrentPage] = useState(0);
  const [deleteId, setDeleteId] = useState<number | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, fetchNextPage, isLoading } = useInfiniteGetAllPurchaseReturn({
    pageSize: 10,
    pageNo: currentPage,
    purchaseReturnNumber: debouncedSearchKey,
  });

  const purchaseData = data?.pages[0]?.purchaseReturnResponseList || [];
  const totalElements = purchaseData.length;
  const totalPages = Math.ceil((Number(data?.pages[0]?.totalPages) || 0) / 10);

  const totalReturnAmount = useMemo(
    () =>
      purchaseData.reduce(
        (sum, item) => sum + Number(item.totalAmount || 0),
        0,
      ),
    [purchaseData],
  );

  const { mutate: deletePurchaseEntryReturn } = useDeletePurchaseEntryReturn();

  const handleDelete = (id: string | undefined) => {
    if (!id) return;
    deletePurchaseEntryReturn(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["useGetAllPurchaseReturn"],
        });
        toast.success("Purchase return entry deleted successfully!");
        setConfirmOpen(false);
        setDeleteId(undefined);
      },
      onError: (error: Error) => {
        toast.error(error.message);
        setConfirmOpen(false);
        setDeleteId(undefined);
      },
    });
  };

  return (
    <div className="min-h-full py-1">
      <div className="space-y-6">
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                Purchase Return
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                Monitor returned purchase items, track vendor-linked return
                references, and open new return entries quickly.
              </p>
            </div>
            <Button
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              Add New Purchase Return
            </Button>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-[300px] max-w-[520px] flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
              <input
                type="text"
                placeholder="Search by vendor or purchase/return number"
                onChange={(e) => setSearchKey(e.target.value)}
                className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
            <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998]">
              <span className="font-semibold text-[#253650]">
                {totalElements}
              </span>{" "}
              purchase returns found
            </div>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#21314A]">
                Purchase Return Directory
              </h2>
              <p className="mt-1 text-sm text-[#7D8998]">
                Review return reference, linked purchase, vendor, and returned
                value from one table.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`rounded-[10px] border border-[#D7E2F5] p-2 text-[#52627C] transition hover:bg-[#F5F8FD] ${currentPage === 0 ? "cursor-not-allowed opacity-50" : ""}`}
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <ChevronLeft className="size-4" />
              </button>
              <div className="rounded-[10px] bg-[#1A5EF9] px-3 py-2 text-sm font-semibold text-white">
                {currentPage + 1}
              </div>
              <button
                className={`rounded-[10px] border border-[#D7E2F5] p-2 text-[#52627C] transition hover:bg-[#F5F8FD] ${purchaseData.length === 0 ? "cursor-not-allowed opacity-50" : ""}`}
                disabled={purchaseData.length === 0}
                onClick={() => {
                  setCurrentPage(currentPage + 1);
                  fetchNextPage();
                }}
              >
                <ChevronLeft className="size-4 rotate-180" />
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="min-h-[240px] flex items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-6 py-4">Return Number</th>
                    <th className="px-6 py-4">Return Date</th>
                    <th className="px-6 py-4">Purchase No</th>
                    <th className="px-6 py-4">Vendor Name</th>
                    <th className="px-6 py-4">VATIN</th>
                    <th className="px-6 py-4 text-right">Return Item</th>
                    <th className="px-6 py-4 text-right">Return Amount</th>
                    <th className="px-6 py-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseData.length > 0 ? (
                    purchaseData.map((item) => (
                      <tr
                        key={item.id}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                      >
                        <td className="px-6 py-4 font-semibold text-[#24344F]">
                          {item.returnNumber}
                        </td>
                        <td className="px-6 py-4">{item.returnDate}</td>
                        <td className="px-6 py-4 font-medium text-[#24344F]">
                          {item.purchaseNumber}
                        </td>
                        <td className="px-6 py-4">{item.vendorName}</td>
                        <td className="px-6 py-4">{item.vendorVatin || "-"}</td>
                        <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                          {item.itemCount}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                          {Number(item.totalAmount || 0).toFixed(3)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() =>
                                navigate(`/purchase-return/edit/${item.id}`)
                              }
                              title="Edit purchase return"
                              aria-label="Edit purchase return"
                              className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                            >
                              <PencilLine className="size-4" />
                              <span className="sr-only">Edit</span>
                            </button>
                            <button
                              onClick={() => {
                                setDeleteId(item.id);
                                setConfirmOpen(true);
                              }}
                              title="Delete purchase return"
                              aria-label="Delete purchase return"
                              className="rounded-[10px] border border-[#F5D4D4] p-2 text-[#B95A5A] transition hover:border-[#EAA7A7] hover:text-[#D14343]"
                            >
                              <Trash className="size-4" />
                              <span className="sr-only">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                      >
                        No purchase returns match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <Modal open={isModalOpen} setOpen={setIsModalOpen}>
          <AddNewPurchaseReturn setOpen={setIsModalOpen} />
        </Modal>
      )}
      <ConfirmPopup
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={() => handleDelete(deleteId ? String(deleteId) : undefined)}
      />
    </div>
  );
}

export default PurchaseReturn;
